import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../BaseComponents/Modal';
import Button from '../../BaseComponents/Button';
import {
  Resource,
  getResourceByIdPost,
  GetResourceByIdResponse,
  getResourceBodySystemLabel,
  getResourceMriConditionLabel,
} from '../../../api/resourceCenter';
import { getFullImageUrl } from '../../../utils/constants';
import './ResourceModal.scss';
import trash from "../../../assets/icons/TrashBin2.svg";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  resource: Resource | null;
  onEdit?: (resource: Resource) => void;
  onDelete?: (resourceId: number) => void;
};

const ViewResourceModal: React.FC<Props> = ({ isOpen, onClose, resource, onEdit, onDelete }) => {
  const [resourceData, setResourceData] = useState<GetResourceByIdResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchResourceDetails = useCallback(async () => {
    if (!resource) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getResourceByIdPost({ resource_id: parseInt(resource.id) });
      setResourceData(response);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch content details');
    } finally {
      setIsLoading(false);
    }
  }, [resource]);

  useEffect(() => {
    if (isOpen && resource) {
      fetchResourceDetails();
    }
  }, [isOpen, resource, fetchResourceDetails]);

  const handleEdit = () => {
    if (onEdit && resource) {
      const resourceForEdit: Resource = {
        id: resourceData?.id.toString() || resource.id,
        name: resourceData?.name || resource.name,
        content: resourceData?.content || resource.content,
        status: resourceData?.status || resource.status,
        biomarkers: resourceData?.biomarkers || resource.biomarkers,
        conditions: resourceData?.conditions || resource.conditions,
        body_systems: resourceData?.body_systems || resource.body_systems,
        mri_conditions: resourceData?.mri_conditions || resource.mri_conditions,
        image: resourceData?.image || resource.image,
        video_url: resourceData?.video_url || resource.video_url,
      };
      onEdit(resourceForEdit);
      onClose();
    }
  };

  const handleDelete = () => {
    if (onDelete && resource) {
      onDelete(parseInt(resource.id));
    }
  };

  if (!resource) return null;

  const bloodBiomarkers =
    resourceData?.biomarkers?.length
      ? resourceData.biomarkers.map((bm) => bm.name).join(', ')
      : resource.biomarkers?.length
        ? resource.biomarkers.map((bm) => bm.name).join(', ')
        : '-';

  const conditions =
    resourceData?.conditions?.length
      ? resourceData.conditions.map((cond) => cond.name).join(', ')
      : resource.conditions?.length
        ? resource.conditions.map((cond) => cond.name).join(', ')
        : '-';

  const bodySystems =
    resourceData?.body_systems?.length
      ? resourceData.body_systems.map(getResourceBodySystemLabel).join(', ')
      : resource.body_systems?.length
        ? resource.body_systems.map(getResourceBodySystemLabel).join(', ')
        : '-';

  const mriConditions =
    resourceData?.mri_conditions?.length
      ? resourceData.mri_conditions.map(getResourceMriConditionLabel).join(', ')
      : resource.mri_conditions?.length
        ? resource.mri_conditions.map(getResourceMriConditionLabel).join(', ')
        : '-';

  const videoUrl = resourceData?.video_url || resource.video_url;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Content"
      width="900px"
    >
      <div className="resource-view-modal">
        {isLoading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading content details...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p className="error-message">{error}</p>
            <Button
              text="Retry"
              onClick={fetchResourceDetails}
              isSecondary={true}
            />
          </div>
        ) : (
          <div className="resource-details">
            <div className="detail-field">
              <label className="field-label">Image</label>
              <div className="field-value image-field">
                {resourceData?.image_url || resource.image ? (
                  <img
                    src={resourceData?.image_url || getFullImageUrl(resource.image || '')}
                    alt={resourceData?.name || resource.name}
                    className="resource-image"
                  />
                ) : (
                  <div className="no-image-placeholder">
                    No image available
                  </div>
                )}
              </div>
            </div>

            <div className="detail-field">
              <label className="field-label">Name</label>
              <div className="field-value">{resourceData?.name || resource.name}</div>
            </div>

            <div className="detail-field">
              <label className="field-label">Blood Biomarkers</label>
              <div className="field-value">{bloodBiomarkers}</div>
            </div>

            <div className="detail-field">
              <label className="field-label">Conditions</label>
              <div className="field-value">{conditions}</div>
            </div>

            <div className="detail-field">
              <label className="field-label">Body Systems</label>
              <div className="field-value">{bodySystems}</div>
            </div>

            <div className="detail-field">
              <label className="field-label">MRI Conditions</label>
              <div className="field-value">{mriConditions}</div>
            </div>

            <div className="detail-field">
              <label className="field-label">Article Text</label>
              <div 
                className="field-value article-content"
                dangerouslySetInnerHTML={{ __html: resourceData?.content || resource.content || '-' }}
              />
            </div>

            <div className="detail-field">
              <label className="field-label">Video Link</label>
              <div className="field-value">
                {videoUrl ? (
                  <a href={videoUrl} target="_blank" rel="noopener noreferrer">
                    {videoUrl}
                  </a>
                ) : (
                  '-'
                )}
              </div>
            </div>

            {resourceData?.created_at && (
              <div className="detail-field">
                <label className="field-label">Created At</label>
                <div className="field-value">
                  {new Date(resourceData.created_at).toLocaleString()}
                </div>
              </div>
            )}

            {resourceData?.updated_at && (
              <div className="detail-field">
                <label className="field-label">Updated At</label>
                <div className="field-value">
                  {new Date(resourceData.updated_at).toLocaleString()}
                </div>
              </div>
            )}
          </div>
        )}

        {!isLoading && !error && (
          <div className="modal-actions">
            <Button
              text="Edit"
              onClick={handleEdit}
              isSecondary={false}
            />
            <button 
              className="delete-button"
              onClick={handleDelete}
            >
              <img src={trash} alt="delete" />
              Delete content
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ViewResourceModal;
